import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Smartphone, Building2, Users } from 'lucide-react';

const AppDownloadSection = () => {
  const [activeTab, setActiveTab] = useState('business');
  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const tabs = [
    { id: 'business', label: 'Business', icon: Building2 },
    { id: 'customer', label: 'Customer', icon: Users },
  ];

  const appData = {
    business: {
      title: 'Business App',
      description: 'Manage your properties, team, appointments, and customer inquiries from anywhere.',
      features: [
        'AI-powered customer chat',
        'Real-time analytics dashboard',
        'Appointment management',
        'Property listings & management',
        'Payment tracking',
      ],
      mockups: [
        { id: 'dashboard', title: 'Dashboard', description: 'Overview of your business metrics' },
        { id: 'properties', title: 'Properties', description: 'Manage all your listings' },
        { id: 'appointments', title: 'Appointments', description: 'Schedule and track visits' },
        { id: 'chat', title: 'AI Chat', description: 'Intelligent customer support' },
      ],
      playStoreUrl: process.env.REACT_APP_BUSINESS_APP_PLAYSTORE_URL || '#',
      appStoreUrl: process.env.REACT_APP_BUSINESS_APP_APPSTORE_URL || '#',
    },
    customer: {
      title: 'Customer App',
      description: 'Find your perfect space, book appointments, and chat with AI assistants.',
      features: [
        'Browse properties with filters',
        'Schedule property visits',
        'Chat with AI assistant',
        'Manage your leases',
        'Pay rent online',
      ],
      mockups: [
        { id: 'home', title: 'Home', description: 'Discover properties' },
        { id: 'property', title: 'Property Details', description: 'View listings in detail' },
        { id: 'chat', title: 'AI Assistant', description: 'Get instant help' },
        { id: 'appointments', title: 'Appointments', description: 'Your scheduled visits' },
      ],
      playStoreUrl: process.env.REACT_APP_CUSTOMER_APP_PLAYSTORE_URL || '#',
      appStoreUrl: process.env.REACT_APP_CUSTOMER_APP_APPSTORE_URL || '#',
    },
  };

  const currentApp = appData[activeTab];

  // Auto-rotate carousel
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % currentApp.mockups.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [activeTab, isPaused, currentApp.mockups.length]);

  // Reset slide when changing tabs
  useEffect(() => {
    setActiveSlide(0);
  }, [activeTab]);

  const handleDotClick = useCallback((index) => {
    setActiveSlide(index);
  }, []);

  return (
    <section id="apps" className="py-24 bg-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 italic" style={{ fontFamily: 'emoji' }}>
            Get the <span className="gradient-text">App</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto italic" style={{ fontFamily: 'emoji' }}>
            Experience seamless management on the go
          </p>
        </motion.div>

        {/* Tab Selector */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex p-1.5 bg-dark-100 rounded-full border border-white/10">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-accent to-accent-dark text-dark'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="glass rounded-3xl p-6 md:p-10 border border-white/10"
        >
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Left Side - Description */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent to-accent-dark flex items-center justify-center">
                  <Smartphone className="text-dark" size={24} />
                </div>
                <h3 className="text-2xl font-bold text-white">{currentApp.title}</h3>
              </div>
              
              <p className="text-gray-400 mb-6 text-lg">
                {currentApp.description}
              </p>

              {/* Features List */}
              <ul className="space-y-3 mb-8">
                {currentApp.features.map((feature, index) => (
                  <motion.li
                    key={feature}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3 text-gray-300"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                    {feature}
                  </motion.li>
                ))}
              </ul>

              {/* Store Buttons */}
              <div className="flex flex-wrap gap-4">
                <a
                  href={currentApp.playStoreUrl}
                  className="flex items-center gap-3 px-6 py-3 bg-dark-200 rounded-xl border border-white/10 hover:border-accent/50 transition-all duration-300 group"
                >
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" className="text-gray-400 group-hover:text-accent transition-colors" />
                  </svg>
                  <div className="text-left">
                    <div className="text-xs text-gray-500">Get it on</div>
                    <div className="text-sm font-semibold text-white">Google Play</div>
                  </div>
                </a>

                <a
                  href={currentApp.appStoreUrl}
                  className="flex items-center gap-3 px-6 py-3 bg-dark-200 rounded-xl border border-white/10 hover:border-accent/50 transition-all duration-300 group"
                >
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" className="text-gray-400 group-hover:text-accent transition-colors" />
                  </svg>
                  <div className="text-left">
                    <div className="text-xs text-gray-500">Download on the</div>
                    <div className="text-sm font-semibold text-white">App Store</div>
                  </div>
                </a>
              </div>
            </div>

            {/* Right Side - Phone Carousel */}
            <div
              className="relative"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div className="relative h-[520px] flex items-center justify-center">
                {/* Carousel */}
                <div className="relative w-full flex items-center justify-center">
                  {currentApp.mockups.map((mockup, index) => {
                    const isActive = index === activeSlide;
                    const isPrev = index === (activeSlide - 1 + currentApp.mockups.length) % currentApp.mockups.length;
                    const isNext = index === (activeSlide + 1) % currentApp.mockups.length;
                    const isVisible = isActive || isPrev || isNext;

                    let scale = 1;
                    let opacity = 1;
                    let zIndex = 10;

                    if (isPrev) {
                      scale = 0.75;
                      opacity = 0.4;
                      zIndex = 5;
                    } else if (isNext) {
                      scale = 0.75;
                      opacity = 0.4;
                      zIndex = 5;
                    } else if (!isActive) {
                      opacity = 0;
                      zIndex = 0;
                    }

                    return (
                      <motion.div
                        key={mockup.id}
                        className="absolute cursor-pointer"
                        style={{ zIndex }}
                        initial={false}
                        animate={{
                          x: isPrev ? -140 : isNext ? 140 : 0,
                          scale,
                          opacity: isVisible ? opacity : 0,
                        }}
                        transition={{ duration: 0.5, ease: 'easeInOut' }}
                        onClick={() => handleDotClick(index)}
                      >
                        {/* Phone Frame */}
                        <div className="relative w-52 md:w-56 h-[420px] md:h-[440px] rounded-[2.5rem] bg-dark-300 border-4 border-dark-400 shadow-2xl overflow-hidden">
                          {/* Phone Notch */}
                          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-dark-400 rounded-b-xl z-10" />
                          
                          {/* Screen Content - Style 1 Minimal Dark */}
                          <div className="absolute inset-2 top-6 rounded-2xl overflow-hidden">
                            <MockupScreen app={activeTab} screen={mockup.id} />
                          </div>
                        </div>

                        {/* Screen Label */}
                        {isActive && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="absolute -bottom-16 left-1/2 -translate-x-1/2 text-center whitespace-nowrap"
                          >
                            <div className="text-white font-semibold">{mockup.title}</div>
                            <div className="text-gray-500 text-sm">{mockup.description}</div>
                          </motion.div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Carousel Dots */}
              <div className="flex justify-center gap-2 mt-8">
                {currentApp.mockups.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleDotClick(index)}
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      index === activeSlide
                        ? 'bg-accent w-8'
                        : 'bg-gray-600 hover:bg-gray-500 w-2.5'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// STYLE 1: Minimal Dark Mockup Screens
// ============================================================================
const MockupScreen = ({ app, screen }) => {
  const bgColor = 'bg-[#0f0f0f]';
  
  if (app === 'business') {
    switch (screen) {
      case 'dashboard':
        return (
          <div className={`${bgColor} h-full p-3`}>
            <div className="text-white text-xs font-semibold mb-3">Dashboard</div>
            {/* Stats Row */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-emerald-500/20 rounded-lg p-2">
                <div className="text-emerald-400 text-[10px]">Revenue</div>
                <div className="text-white text-sm font-bold">$12,450</div>
              </div>
              <div className="bg-blue-500/20 rounded-lg p-2">
                <div className="text-blue-400 text-[10px]">Properties</div>
                <div className="text-white text-sm font-bold">24</div>
              </div>
            </div>
            {/* Chart Placeholder */}
            <div className="bg-dark-200 rounded-lg p-2 mb-3">
              <div className="text-gray-500 text-[10px] mb-2">Weekly Overview</div>
              <div className="flex items-end justify-between h-20 gap-1">
                {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                  <div key={i} className="flex-1 bg-emerald-500/40 rounded-t" style={{ height: `${h}%` }} />
                ))}
              </div>
            </div>
            {/* Recent Activity */}
            <div className="text-gray-500 text-[10px] mb-1">Recent Activity</div>
            <div className="space-y-1.5">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-dark-200 rounded-lg p-2 flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-emerald-500/50" />
                  </div>
                  <div className="flex-1">
                    <div className="h-2 bg-gray-700 rounded w-3/4" />
                    <div className="h-1.5 bg-gray-800 rounded w-1/2 mt-1" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'properties':
        return (
          <div className={`${bgColor} h-full p-3`}>
            <div className="text-white text-xs font-semibold mb-3">Properties</div>
            {/* Search */}
            <div className="bg-dark-200 rounded-lg p-2.5 mb-3 flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gray-600 flex items-center justify-center text-[8px] text-gray-400">🔍</div>
              <div className="h-2 bg-gray-700 rounded flex-1" />
            </div>
            {/* Property Cards */}
            <div className="space-y-2.5">
              {[
                { gradient: 'from-emerald-900/40 to-teal-900/40', price: '$1,200/mo', status: 'Active' },
                { gradient: 'from-blue-900/40 to-indigo-900/40', price: '$950/mo', status: 'Active' },
                { gradient: 'from-purple-900/40 to-pink-900/40', price: '$1,450/mo', status: 'Pending' },
              ].map((item, i) => (
                <div key={i} className="bg-dark-200 rounded-xl overflow-hidden">
                  <div className={`h-16 bg-gradient-to-r ${item.gradient}`} />
                  <div className="p-2.5">
                    <div className="h-2 bg-white/80 rounded w-3/4 mb-1" />
                    <div className="h-1.5 bg-gray-600 rounded w-1/2 mb-2" />
                    <div className="flex justify-between items-center">
                      <div className="text-emerald-400 text-[11px] font-bold">{item.price}</div>
                      <div className={`px-2 py-0.5 rounded text-[8px] ${item.status === 'Active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{item.status}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'appointments':
        return (
          <div className={`${bgColor} h-full p-3`}>
            <div className="text-white text-xs font-semibold mb-3">Appointments</div>
            {/* Calendar Mini */}
            <div className="bg-dark-200 rounded-lg p-2.5 mb-3">
              <div className="grid grid-cols-7 gap-1 text-center text-[8px] text-gray-500 mb-1.5">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                  <div key={i}>{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-[9px]">
                {Array.from({ length: 28 }, (_, i) => (
                  <div
                    key={i}
                    className={`py-1 rounded ${
                      i === 14 ? 'bg-emerald-500 text-dark font-bold' : 
                      [8, 16, 22].includes(i) ? 'bg-emerald-500/20 text-emerald-400' : 'text-gray-400'
                    }`}
                  >
                    {i + 1}
                  </div>
                ))}
              </div>
            </div>
            {/* Appointments List */}
            <div className="text-gray-500 text-[10px] mb-1.5">Today's Schedule</div>
            <div className="space-y-2">
              {[
                { time: '9:00 AM', title: 'Property Viewing', type: 'viewing' },
                { time: '2:30 PM', title: 'Lease Signing', type: 'signing' },
                { time: '5:00 PM', title: 'Maintenance Check', type: 'maintenance' },
              ].map((apt, i) => (
                <div key={i} className="bg-dark-200 rounded-lg p-2.5 flex items-center gap-2">
                  <div className="text-emerald-400 text-[10px] font-mono w-14">{apt.time}</div>
                  <div className="flex-1">
                    <div className="text-white text-[11px] font-medium">{apt.title}</div>
                    <div className="h-1.5 bg-gray-700 rounded w-2/3 mt-1" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      case 'chat':
        return (
          <div className={`${bgColor} h-full p-3 flex flex-col`}>
            <div className="text-white text-xs font-semibold mb-3">AI Assistant</div>
            <div className="flex-1 space-y-2.5 overflow-hidden">
              {/* AI Message */}
              <div className="flex gap-2">
                <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center text-[9px] text-dark font-bold shrink-0">AI</div>
                <div className="bg-dark-200 rounded-xl rounded-tl-none p-2.5 max-w-[85%]">
                  <div className="text-[11px] text-gray-300">Hello! How can I help you manage your properties today?</div>
                </div>
              </div>
              {/* User Message */}
              <div className="flex gap-2 justify-end">
                <div className="bg-emerald-500/20 rounded-xl rounded-tr-none p-2.5 max-w-[85%]">
                  <div className="text-[11px] text-emerald-300">Show me today's appointments</div>
                </div>
              </div>
              {/* AI Message */}
              <div className="flex gap-2">
                <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center text-[9px] text-dark font-bold shrink-0">AI</div>
                <div className="bg-dark-200 rounded-xl rounded-tl-none p-2.5 max-w-[85%]">
                  <div className="text-[11px] text-gray-300">You have 3 appointments scheduled:</div>
                  <div className="mt-1.5 space-y-1">
                    <div className="text-[10px] text-emerald-400">• 9:00 AM - Property Viewing</div>
                    <div className="text-[10px] text-emerald-400">• 2:30 PM - Lease Signing</div>
                    <div className="text-[10px] text-emerald-400">• 5:00 PM - Maintenance</div>
                  </div>
                </div>
              </div>
            </div>
            {/* Input */}
            <div className="mt-3 bg-dark-200 rounded-full p-2 flex items-center gap-2">
              <div className="h-2 bg-gray-700 rounded flex-1 ml-2" />
              <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center">
                <div className="w-0 h-0 border-t-4 border-t-transparent border-l-6 border-l-dark border-b-4 border-b-transparent ml-0.5" />
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  }

  // Customer App Screens
  switch (screen) {
    case 'home':
      return (
        <div className={`${bgColor} h-full p-3`}>
          <div className="text-white text-xs font-semibold mb-3">Find Your Home</div>
          {/* Search */}
          <div className="bg-dark-200 rounded-lg p-2.5 mb-3">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-purple-500/30 flex items-center justify-center text-[8px]">🔍</div>
              <div className="h-2 bg-gray-700 rounded flex-1" />
            </div>
          </div>
          {/* Categories */}
          <div className="flex gap-2 mb-3 overflow-hidden">
            {['Apartment', 'House', 'Studio', 'Condo'].map((cat, i) => (
              <div key={i} className={`px-2.5 py-1 rounded-full text-[9px] whitespace-nowrap ${i === 0 ? 'bg-purple-500 text-white' : 'bg-dark-200 text-gray-400'}`}>
                {cat}
              </div>
            ))}
          </div>
          {/* Featured */}
          <div className="text-gray-500 text-[10px] mb-1.5">Featured Properties</div>
          <div className="space-y-2.5">
            {[
              { gradient: 'from-purple-900/40 to-pink-900/40', price: '$950/mo', beds: '2 bed', baths: '1 bath' },
              { gradient: 'from-cyan-900/40 to-blue-900/40', price: '$1,200/mo', beds: '3 bed', baths: '2 bath' },
            ].map((item, i) => (
              <div key={i} className="bg-dark-200 rounded-xl overflow-hidden">
                <div className={`h-20 bg-gradient-to-br ${item.gradient} relative`}>
                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-dark/60 flex items-center justify-center">
                    <span className="text-[10px]">♡</span>
                  </div>
                </div>
                <div className="p-2.5">
                  <div className="h-2 bg-white/80 rounded w-3/4 mb-1" />
                  <div className="flex items-center gap-2 mb-1">
                    <div className="text-purple-400 text-[11px] font-bold">{item.price}</div>
                    <div className="text-gray-500 text-[9px]">• {item.beds} • {item.baths}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    case 'property':
      return (
        <div className={`${bgColor} h-full`}>
          {/* Image */}
          <div className="h-36 bg-gradient-to-br from-blue-900/50 to-purple-900/50 relative">
            <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-dark/60 flex items-center justify-center text-[10px]">←</div>
            <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-dark/60 flex items-center justify-center text-[10px]">♡</div>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === 1 ? 'bg-white' : 'bg-white/40'}`} />
              ))}
            </div>
          </div>
          <div className="p-3">
            <div className="text-white text-sm font-semibold mb-1">Modern Apartment</div>
            <div className="text-gray-500 text-[10px] mb-3">123 Main St, Downtown</div>
            <div className="flex gap-4 mb-3">
              {[
                { label: '2 Bed', icon: '🛏' },
                { label: '1 Bath', icon: '🚿' },
                { label: '850 sqft', icon: '📐' }
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="text-base">{item.icon}</div>
                  <div className="text-[9px] text-gray-400">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="text-purple-400 text-xl font-bold mb-3">$1,200/mo</div>
            <div className="bg-purple-500 rounded-xl py-2.5 text-center text-white text-xs font-semibold">
              Schedule Visit
            </div>
          </div>
        </div>
      );
    case 'chat':
      return (
        <div className={`${bgColor} h-full p-3 flex flex-col`}>
          <div className="text-white text-xs font-semibold mb-3">AI Assistant</div>
          <div className="flex-1 space-y-2.5 overflow-hidden">
            <div className="flex gap-2">
              <div className="w-7 h-7 rounded-full bg-purple-500 flex items-center justify-center text-[9px] text-white font-bold shrink-0">AI</div>
              <div className="bg-dark-200 rounded-xl rounded-tl-none p-2.5 max-w-[85%]">
                <div className="text-[11px] text-gray-300">Hi! I can help you find your perfect home. What are you looking for?</div>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <div className="bg-purple-500/20 rounded-xl rounded-tr-none p-2.5 max-w-[85%]">
                <div className="text-[11px] text-purple-300">2 bedroom apartment under $1500</div>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="w-7 h-7 rounded-full bg-purple-500 flex items-center justify-center text-[9px] text-white font-bold shrink-0">AI</div>
              <div className="bg-dark-200 rounded-xl rounded-tl-none p-2.5 max-w-[85%]">
                <div className="text-[11px] text-gray-300">I found 12 properties matching your criteria! Here are the top picks:</div>
                <div className="mt-1.5 space-y-1">
                  <div className="text-[10px] text-purple-400">• Downtown Loft - $1,200</div>
                  <div className="text-[10px] text-purple-400">• Urban Studio - $1,350</div>
                  <div className="text-[10px] text-purple-400">• Garden View - $1,450</div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-3 bg-dark-200 rounded-full p-2 flex items-center gap-2">
            <div className="h-2 bg-gray-700 rounded flex-1 ml-2" />
            <div className="w-7 h-7 rounded-full bg-purple-500 flex items-center justify-center">
              <div className="w-0 h-0 border-t-4 border-t-transparent border-l-6 border-l-white border-b-4 border-b-transparent ml-0.5" />
            </div>
          </div>
        </div>
      );
    case 'appointments':
      return (
        <div className={`${bgColor} h-full p-3`}>
          <div className="text-white text-xs font-semibold mb-3">My Appointments</div>
          {/* Tabs */}
          <div className="flex gap-2 mb-3">
            <div className="px-3 py-1.5 bg-purple-500 rounded-full text-[10px] text-white font-medium">Upcoming</div>
            <div className="px-3 py-1.5 bg-dark-200 rounded-full text-[10px] text-gray-400">Past</div>
          </div>
          {/* Appointments */}
          <div className="space-y-2.5">
            {[
              { date: 'Today', time: '2:00 PM', property: 'Modern Apartment', status: 'Confirmed' },
              { date: 'Tomorrow', time: '10:00 AM', property: 'Downtown Loft', status: 'Pending' },
              { date: 'Jan 20', time: '3:30 PM', property: 'Urban Studio', status: 'Confirmed' },
            ].map((apt, i) => (
              <div key={i} className="bg-dark-200 rounded-xl p-2.5">
                <div className="flex justify-between items-start mb-1.5">
                  <div>
                    <div className="text-white text-[11px] font-medium">{apt.property}</div>
                    <div className="h-1.5 bg-gray-700 rounded w-20 mt-1" />
                  </div>
                  <div className={`px-2 py-0.5 rounded text-[8px] font-medium ${apt.status === 'Confirmed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {apt.status}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <div className="text-gray-500 text-[10px]">{apt.date}</div>
                  <div className="w-1 h-1 rounded-full bg-gray-600" />
                  <div className="text-purple-400 text-[10px] font-medium">{apt.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    default:
      return null;
  }
};

export default AppDownloadSection;
